/**
 * 게임 카드 단독 import/export — 결정 #6(포맷 교환) + #29(데이터형 게임 카드) +
 * #23(공유 메타). 사용자가 만든 데이터형 게임을 JSON으로 공유하고, 받는 쪽이
 * 가져와 즉시 플레이하게 한다. 공식 페이로드는 `.rqg` 게임 문서이며, 오래된
 * `roqeet-game_v1`/평면 GameDefinition은 import 시 마이그레이션만 지원한다.
 *
 * 플랫폼 중립(결정 #10): 문자열 ↔ RoQeet game document 변환 + 검증만. import는
 * createGameCard로 새 id/sync 메타를 부여하고 definition.id를 카드 id로 재시드해
 * 붙여넣기 시 moduleId 충돌을 막는다(결정 #29).
 *
 * 보안(결정 #17): import는 알 수 없는/위험한 필드를 받아들이지 않고, 화이트리스트
 * 형태로 GameDefinition을 재구성한다. 실행 코드가 전혀 없는 선언적 데이터이므로
 * 주입 위험은 없지만, 구조 검증으로 깨진 카드가 컴파일 단계에서 터지지 않게 한다.
 */
import type { GameCard, ContentRating } from "../domain/index.js";
import type {
  GameAssetBinding,
  GameAssetRegistryEntry,
  GameClamp,
  GameCondition,
  GameConditionOp,
  GameDefinition,
  GameDiceRule,
  GameEndCondition,
  GameEntityRoster,
  GameEntityTemplate,
  GameGate,
  GameHudDeclaration,
  GameRule,
  GameRuleAction,
  GameSetupField,
  GameSetupOption,
  GameStatBarBinding,
  GameStatPanelBinding,
  GameStateFieldSchema,
  GameStateValueType,
  GameViewBinding,
} from "../game/game-definition.js";
import {
  formatRoqeetGameDocument,
  gameDefinitionToRoqeetJson,
  isRoqeetGameDocument,
  parseRoqeetGameDocument,
  roqeetJsonToGameDefinition,
  validateRoqeetGameDocument,
  type RoqeetGameDocument,
  type RoqeetGameSource,
} from "../game/roqeet-game-document.js";
import { analyzeRqtPack } from "./rqtpack.js";
import { isZip, readZipEntries, readZipEntryData, ZipEntrySizeLimitError, type ZipEntry } from "./zip.js";
import { createGameCard } from "../factory/entities.js";
import { runRoqeet, type RoqeetRunResult, type RoqeetStateDeclaration, type RoqeetValue } from "../roqeet/index.js";
import { rqtToRoqeetGameDocument } from "../roqeet/rqg-lowerer.js";

type AnyObj = Record<string, unknown>;

const CONDITION_OPS = new Set<GameConditionOp>([
  "lte",
  "gte",
  "lt",
  "gt",
  "eq",
  "neq",
  "truthy",
  "falsy",
]);
const RULE_ACTION_OPS = new Set(["set", "inc", "dec"]);
const STATE_VALUE_TYPES = new Set<GameStateValueType>(["number", "string", "boolean", "list", "array", "string[]", "object", "enum"]);
const ASSET_KINDS = new Set(["image", "audio", "sfx", "video", "other"]);
const ASSET_SLOTS = new Set(["background", "sprite", "bgm", "sfx", "overlay"]);
const ASSET_POSITIONS = new Set(["left", "center", "right"]);
const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder("utf-8");
const MIB = 1024 * 1024;
const RQP_TEXT_ENTRY_LIMIT_BYTES = 5 * MIB;
const RQP_TOTAL_TEXT_ENTRY_LIMIT_BYTES = 10 * MIB;
const RQT_DOCUMENT_META_MARKER = "roqeet:document-json-v1";
const RQT_DICE_RUNTIME_KEYS = new Set(["lastRoll", "lastRollNotation", "lastRollSuccess"]);
const RQT_DERIVED_SYSTEM_KEYS = new Set(["completed", "failed", "ended"]);
const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/**
 * 게임 카드를 단독 JSON 문자열로 직렬화한다(결정 #6/#29). 휘발성 메타(id/sync)와
 * 설치 로컬 상태(enabled)는 빼고 공유 가능한 definition + share/rating만 담는다.
 */
export function exportGameCardToJson(card: GameCard): string {
  const document = card.document ?? gameDefinitionToRoqeetJson(card.definition);
  const data: AnyObj = { document };
  if (card.share) data["share"] = card.share;
  if (card.rating) data["rating"] = card.rating;
  return JSON.stringify({ spec: "roqeet-game_v2", data }, null, 2);
}

/** Official single-game export: a RoQeet Game runtime document (`.rqg`). */
export function exportGameCardToRqg(card: GameCard): string {
  const document = roqeetGameDocumentForExport(card);
  assertExportableRoqeetGameDocument(document);
  return formatRoqeetGameDocument(document);
}

/** Optional human-authored `.rqt` source export when the card has current source. */
export function exportGameCardToRqt(card: GameCard): string | undefined {
  return sourceRqtForCard(card);
}

/** Official finished-game export: a ZIP-based RoQeet Package (`.rqp`). */
export function exportGameCardToRqp(card: GameCard): Uint8Array {
  const rqg = exportGameCardToRqg(card);
  const sourceRqt = sourceRqtForCard(card);
  const title = card.definition.title.trim() || "Untitled Game";
  const packageId = packageIdForGameCard(card);
  const manifest = {
    id: packageId,
    name: title,
    version: card.share?.creatorVersion ?? "0.1.0",
    packVersion: "0.2",
    languageVersion: "0.2",
    type: "game",
    description: card.definition.description,
    publisher: {
      ...(card.share?.author ? { name: card.share.author } : {}),
      ...(card.share?.authorId ? { id: card.share.authorId } : {}),
      ...(card.share?.sourceUrl ? { sourceUrl: card.share.sourceUrl } : {}),
    },
    entry: {
      game: "game.rqg",
      ...(sourceRqt ? { script: "source/main.rqt" } : {}),
    },
    compatibility: {
      minLauncherVersion: "0.2",
    },
    contentRating: {
      rating: card.rating ?? "unrated",
      requiresAgeGate: card.rating === "nsfw",
    },
    recommendations: {
      openWith: "roqeet-launcher",
      moduleId: card.definition.id,
    },
    assets: Object.fromEntries(
      (card.definition.assets ?? []).map((asset) => [
        asset.key,
        {
          kind: asset.kind,
          ...(asset.label ? { label: asset.label } : {}),
          ...(asset.assetId ? { ref: asset.assetId } : {}),
        },
      ]),
    ),
    ...(card.share?.license ? { license: card.share.license } : {}),
    ...(card.share?.tags?.length ? { tags: card.share.tags } : {}),
  };
  const readme = [
    `# ${title}`,
    "",
    card.definition.description ?? "RoQeet game package.",
    "",
    sourceRqt
      ? "This .rqp contains manifest.json, game.rqg, and source/main.rqt for RoQeet Launcher."
      : "This .rqp contains manifest.json and game.rqg for RoQeet Launcher.",
  ].join("\n");
  return makeStoredZip([
    { name: "manifest.json", data: JSON.stringify(manifest, null, 2) },
    { name: "game.rqg", data: rqg },
    ...(sourceRqt ? [{ name: "source/main.rqt", data: sourceRqt }] : []),
    { name: "README.md", data: readme },
  ]);
}

export function importGameCardFromRqg(text: string): GameCard {
  return importGameCardFromJson(text);
}

export async function importGameCardFromRqp(bytes: Uint8Array): Promise<GameCard> {
  if (!isZip(bytes)) {
    throw new Error(".rqp 패키지는 ZIP 형식이어야 합니다.");
  }
  const analysis = await analyzeRqtPack(bytes);
  if (!analysis.installable || !analysis.manifest) {
    const details = [...analysis.errors, ...analysis.warnings].map((issue) => issue.message).join(" ");
    throw new Error(details || ".rqp 패키지를 설치할 수 없습니다.");
  }
  const manifest = analysis.manifest;
  const gamePath = manifest.entry.game;
  if (!gamePath) {
    throw new Error(".rqp manifest에 entry.game이 없습니다.");
  }
  const entries = readZipEntries(bytes);
  const entry = entries.find((item) => item.name === gamePath);
  if (!entry) {
    throw new Error(".rqp 패키지에서 게임 문서를 찾을 수 없습니다.");
  }
  const sourcePath = manifest.entry.script;
  const sourceEntry = sourcePath
    ? entries.find((item) => item.name === sourcePath)
    : undefined;
  assertRqpRelevantTextEntriesWithinLimit([entry, sourceEntry].filter((item): item is ZipEntry => !!item));
  assertRqpTextEntryWithinLimit(entry, "game.rqg");
  const data = await readRqpTextEntryData(bytes, entry, "game.rqg");
  if (!data) {
    throw new Error(".rqp 패키지의 게임 문서를 읽을 수 없습니다.");
  }
  assertRqpTextBytesWithinLimit(data, "game.rqg");
  const card = importGameCardFromJson(TEXT_DECODER.decode(data));
  const sourceRqt = await readPackageSourceRqt(bytes, sourcePath, entries);
  const share = {
    ...(card.share ?? {}),
    ...(typeof manifest.publisher?.["name"] === "string"
      ? { author: manifest.publisher["name"] as string }
      : {}),
    ...(typeof manifest["license"] === "string"
      ? { license: manifest["license"] as string }
      : {}),
    ...(Array.isArray(manifest["tags"])
      ? { tags: manifest["tags"].map((tag) => String(tag)) }
      : {}),
    creatorVersion: manifest.version,
  };
  return {
    ...card,
    extensions: {
      ...(card.extensions ?? {}),
      ...(sourceRqt ? { sourceRqt, sourceRqtStatus: "current" } : {}),
    },
    share: Object.keys(share).length ? share : card.share,
    rating: ratingFromPackage(manifest.contentRating?.["rating"]) ?? card.rating,
  };
}

/**
 * JSON 텍스트에서 게임 카드를 임포트한다(결정 #6/#29). 공식 `.rqg` 게임 문서와
 * 래퍼(spec=roqeet-game_v2)를 우선 지원하고, 기존 roqeet-game_v1/평면 GameDefinition은
 * 새 문서로 마이그레이션한다. createGameCard가 새 id를 부여하고 definition.id를
 * 재시드하므로 기존 카드/빌트인과 충돌하지 않는다.
 */
export function importGameCardFromJson(text: string): GameCard {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("게임 카드 JSON을 해석할 수 없습니다.");
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("게임 카드 JSON은 객체여야 합니다.");
  }
  const root = parsed as AnyObj;

  if (isRoqeetGameDocument(root)) {
    const document = root;
    const definition = validatedRoqeetGameDocumentToDefinition(document);
    return createGameCard({
      definition,
      document,
      enabled: true,
    });
  }

  // 공식 래퍼 형태: data.document가 `.rqg` 게임 문서다.
  if (root["spec"] === "roqeet-game_v2" && root["data"] && typeof root["data"] === "object") {
    const data = root["data"] as AnyObj;
    const document = data["document"];
    if (!isRoqeetGameDocument(document)) {
      throw new Error("유효한 게임 카드가 아닙니다(.rqg document 없음).");
    }
    const definition = validatedRoqeetGameDocumentToDefinition(document);
    const init: Partial<GameCard> & { definition: GameDefinition; document: RoqeetGameDocument } = {
      definition,
      document,
      enabled: true,
    };
    if (data["share"] && typeof data["share"] === "object") {
      init.share = data["share"] as GameCard["share"];
    }
    if (typeof data["rating"] === "string") {
      init.rating = data["rating"] as ContentRating;
    }
    return createGameCard(init);
  }

  // 오래된 래퍼 형태 / data.definition / 평면 definition 모두 수용(마이그레이션).
  let data: AnyObj;
  if (root["spec"] === "roqeet-game_v1" && root["data"] && typeof root["data"] === "object") {
    data = root["data"] as AnyObj;
  } else {
    data = root;
  }
  const defSource: AnyObj =
    data["definition"] && typeof data["definition"] === "object"
      ? (data["definition"] as AnyObj)
      : data;

  const definition = sanitizeDefinition(defSource);
  const document = gameDefinitionToRoqeetJson(definition);

  const init: Partial<GameCard> & { definition: GameDefinition; document: RoqeetGameDocument } = {
    definition,
    document,
    // 가져온 카드는 기본 활성(바로 플레이 가능). 공유/등급 메타는 있으면 보존.
    enabled: true,
  };
  if (data["share"] && typeof data["share"] === "object") {
    init.share = data["share"] as GameCard["share"];
  }
  if (typeof data["rating"] === "string") {
    init.rating = data["rating"] as ContentRating;
  }

  return createGameCard(init);
}

/**
 * 코드 모드(#30)용: 공식 `.rqg` 게임 문서를 검증해 정의로 변환한다. 폼이
 * 노출하지 않는 부분까지 개발자가 직접 편집한 JSON을 안전하게 받아들인다.
 */
export function importGameCardFromRqt(source: string, filename = "main.rqt"): GameCard {
  const definition = roqeetRqtToGameDefinition(source, filename);
  return createGameCard({
    definition,
    document: gameDefinitionToRoqeetJson(definition),
    extensions: { sourceRqt: source, sourceRqtStatus: "current" },
    enabled: true,
  });
}

export function roqeetRqtToGameDefinition(
  source: string,
  filename = "main.rqt",
  options: { baseDefinition?: GameDefinition } = {},
): GameDefinition {
  const embeddedDocument = readEmbeddedRqtGameDocument(source);
  const baseDefinition = options.baseDefinition ?? (embeddedDocument ? roqeetJsonToGameDefinition(embeddedDocument) : undefined);
  const imported = roqeetRqtToLooseGameDefinition(source, filename, {
    appendDefaultGuide: !baseDefinition,
  });
  const importedWithSceneGraph = attachValidatedRqtSceneGraph(imported, source, filename);
  if (!baseDefinition) return importedWithSceneGraph;
  return mergeRqtDraftWithBaseDefinition(baseDefinition, importedWithSceneGraph);
}

function roqeetRqtToLooseGameDefinition(
  source: string,
  filename: string,
  options: { appendDefaultGuide: boolean },
): GameDefinition {
  const result = runRoqeet(source, {}, { fileId: filename, invalidationReason: "rqt_game_import" });
  const fatal = result.errors.find((error) => error.code !== "UNCLOSED_BLOCK");
  if (fatal) {
    throw new Error(`.rqt parse error at line ${fatal.line}: ${fatal.message}`);
  }

  const env = collectRqtEnv(result);
  const gameMeta = collectRqtGameMeta(result);
  const declaredState = collectRqtStateSchema(result);
  const runtimeState = Object.keys(result.state).length ? cloneRecord(result.state) : {};
  const initialState: Record<string, unknown> = Object.keys(declaredState.initialState).length || Object.keys(runtimeState).length
    ? { ...declaredState.initialState, ...runtimeState }
    : { turn: 0, ended: false };
  const title =
    stringEnv(gameMeta, ["title"]) ??
    stringEnv(env, ["title", "gameTitle", "name", "\uC81C\uBAA9", "\uC774\uB984"]) ??
    titleFromFilename(filename);
  const description =
    stringEnv(gameMeta, ["description"]) ??
    stringEnv(env, ["description", "summary", "\uC124\uBA85", "\uC694\uC57D"]);
  const summary = stringEnv(env, ["stateSummary", "\uC0C1\uD0DC\uC694\uC57D"]);
  const mutableKeys =
    stringArrayEnv(env, ["mutableKeys", "mutable", "\uBCC0\uACBD\uAC00\uB2A5"]) ??
    (declaredState.fields.length ? declaredState.fields.filter(isRqtAiMutableField).map((field) => field.key) : Object.keys(initialState));
  const assets = buildRqtAssets(result);
  const assetBindings = buildRqtAssetBindings(result);

  return {
    id: "",
    title,
    ...(description ? { description } : {}),
    icon: stringEnv(gameMeta, ["icon"]) ?? stringEnv(env, ["icon", "\uC544\uC774\uCF58"]) ?? "game",
    initialState,
    instructions: buildRqtInstructions(result, env, options.appendDefaultGuide),
    mutableKeys: Array.from(new Set(mutableKeys)),
    ...(declaredState.fields.length ? { stateSchema: declaredState.fields } : {}),
    stateSummaryTemplate: summary ?? defaultStateSummary(initialState),
    ...(result.rolls.length || initialState["lastRoll"] !== undefined ? { dice: {} } : {}),
    ...(initialState["ended"] !== undefined
      ? { endWhen: [{ key: "ended", op: "truthy", label: "Ended", tone: "success" }] }
      : {}),
    ...(assets.length ? { assets } : {}),
    ...(assetBindings.length ? { assetBindings } : {}),
    view: buildRqtView(initialState, result),
  };
}

export function parseRoqeetGameSource(text: string): RoqeetGameSource {
  const document = parseRoqeetGameDocument(text);
  return { document, definition: roqeetJsonToGameDefinition(document) };
}

function validatedRoqeetGameDocumentToDefinition(document: RoqeetGameDocument): GameDefinition {
  const errors = validateRoqeetGameDocument(document).filter((issue) => issue.severity === "error");
  if (errors.length > 0) {
    throw new Error(
      `.rqg document validation failed: ${errors
        .slice(0, 3)
        .map((issue) => issue.path ? `${issue.path}: ${issue.message}` : issue.message)
        .join(" / ")}`,
    );
  }
  return roqeetJsonToGameDefinition(document);
}

function attachValidatedRqtSceneGraph(
  definition: GameDefinition,
  source: string,
  filename: string,
): GameDefinition {
  const lowered = rqtToRoqeetGameDocument(source, { fileId: filename });
  if (lowered.parseErrors.length > 0) return definition;
  const errors = lowered.validationIssues.filter((issue) => issue.severity === "error");
  if (errors.length > 0) return definition;

  const loweredDefinition = roqeetJsonToGameDefinition(lowered.document);
  if (!loweredDefinition.sceneGraph?.scenes.length) return definition;
  if (!hasStatefulSceneChoices(loweredDefinition)) {
    return {
      ...definition,
      sceneGraph: loweredDefinition.sceneGraph,
    };
  }
  const currentSummary = definition.stateSummaryTemplate?.trim();
  const currentDefaultSummary = defaultStateSummary(definition.initialState).trim();
  return {
    ...definition,
    initialState: cloneRecord(loweredDefinition.initialState),
    ...(loweredDefinition.stateSchema?.length ? { stateSchema: loweredDefinition.stateSchema } : {}),
    stateSummaryTemplate:
      currentSummary && currentSummary !== currentDefaultSummary
        ? definition.stateSummaryTemplate
        : defaultStateSummary(loweredDefinition.initialState),
    sceneGraph: loweredDefinition.sceneGraph,
  };
}

function hasStatefulSceneChoices(definition: GameDefinition): boolean {
  return (definition.sceneGraph?.scenes ?? []).some((scene) =>
    (scene.choices ?? []).some((choice) => !!choice.effects?.length || !!choice.when?.length)
  );
}

/**
 * Best-effort advanced authoring export for `.rqt`.
 *
 * `.rqg` remains the exact saved document format. The visible DSL is
 * intentionally small and human-editable. Editors that need exact round-trip
 * context should keep the source `.rqg` document outside the visible DSL.
 */
export function gameDefinitionToRqtDraft(
  definition: GameDefinition,
  _sourceDocument?: RoqeetGameDocument,
): string {
  const lines = [
    "#!roqeet/rqt 0.2",
    "",
    formatRqtGameLine(definition),
  ];
  if (definition.stateSummaryTemplate) {
    lines.push("", "@env {");
    lines.push(`  stateSummary = ${jsonLiteral(normalizeRqtTemplateSyntax(definition.stateSummaryTemplate))}`);
    lines.push("}");
  }
  lines.push("");

  const schemaByKey = new Map((definition.stateSchema ?? []).map((field) => [field.key, field]));
  const emitted = new Set<string>();
  for (const field of definition.stateSchema ?? []) {
    if (RQT_DICE_RUNTIME_KEYS.has(field.key)) continue;
    const initial = definition.initialState[field.key] ?? field.default;
    const line = formatRqtStateLine(field, initial);
    if (!line) continue;
    lines.push(line);
    emitted.add(field.key);
  }
  for (const [key, value] of Object.entries(definition.initialState ?? {})) {
    if (emitted.has(key)) continue;
    const field = schemaByKey.get(key);
    if (field?.transient || field?.derived || RQT_DICE_RUNTIME_KEYS.has(key)) continue;
    if (RQT_DERIVED_SYSTEM_KEYS.has(key) && field?.mutableBy !== "ai") continue;
    if (Array.isArray(value)) {
      for (const item of value) lines.push(`@push ${key} ${jsonLiteral(String(item))}`);
      continue;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      lines.push(`@set ${key} = ${String(value)}`);
    } else if (value !== null && typeof value !== "object") {
      lines.push(`@set ${key} = ${jsonLiteral(String(value ?? ""))}`);
    }
  }

  if (definition.instructions?.trim()) {
    lines.push("", '@prompt "guide" {');
    for (const line of definition.instructions.split(/\r?\n/)) lines.push(`  ${line}`);
    lines.push("}");
  }
  lines.push("");
  return lines.join("\n");
}

function formatRqtGameLine(definition: GameDefinition): string {
  return formatRqtCommandWithAttrs("@game", [
    ["title", definition.title || "Untitled Game"],
    ...(definition.description ? [["description", definition.description] as const] : []),
    ...(definition.icon ? [["icon", definition.icon] as const] : []),
  ]);
}

function formatRqtStateLine(field: GameStateFieldSchema, initial: unknown): string | undefined {
  const valueType = field.valueType;
  if (valueType !== "number" && valueType !== "string" && valueType !== "boolean" && valueType !== "enum") {
    return undefined;
  }
  if (
    (valueType === "number" && typeof initial !== "number") ||
    (valueType === "boolean" && typeof initial !== "boolean") ||
    ((valueType === "string" || valueType === "enum") && typeof initial !== "string")
  ) {
    return undefined;
  }
  if (typeof initial !== "string" && typeof initial !== "number" && typeof initial !== "boolean") {
    return undefined;
  }
  const attrs: Array<readonly [string, string | number | boolean]> = [["initial", initial]];
  if (typeof field.min === "number") attrs.push(["min", field.min]);
  if (typeof field.max === "number") attrs.push(["max", field.max]);
  if (field.values?.length) attrs.push(["values", field.values.join(",")]);
  if (field.mutableBy) attrs.push(["mutableBy", field.mutableBy]);
  if (typeof field.mutable === "boolean") attrs.push(["mutable", field.mutable]);
  if (typeof field.derived === "boolean") attrs.push(["derived", field.derived]);
  if (typeof field.transient === "boolean") attrs.push(["transient", field.transient]);
  if (field.label) attrs.push(["label", field.label]);
  return formatRqtCommandWithAttrs(`@state ${field.key} ${valueType}`, attrs);
}

function formatRqtCommandWithAttrs(
  command: string,
  attrs: Array<readonly [string, string | number | boolean]>,
): string {
  return [command, ...attrs.map(([key, value]) => `${key}=${formatRqtAttributeValue(value)}`)].join(" ");
}

function formatRqtAttributeValue(value: string | number | boolean): string {
  return typeof value === "string" ? jsonLiteral(value) : String(value);
}

function readEmbeddedRqtGameDocument(source: string): RoqeetGameDocument | undefined {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  for (let i = 0; i < lines.length; i++) {
    if (lines[i]?.trim() !== "@comment {") continue;
    if (lines[i + 1]?.trim() !== RQT_DOCUMENT_META_MARKER) continue;
    const chunks: string[] = [];
    for (let j = i + 2; j < lines.length; j++) {
      const trimmed = lines[j]?.trim() ?? "";
      if (trimmed === "}") {
        try {
          const parsed = JSON.parse(decodeURIComponent(chunks.join("")));
          return isRoqeetGameDocument(parsed) ? parsed : undefined;
        } catch {
          return undefined;
        }
      }
      chunks.push(trimmed);
    }
  }
  return undefined;
}

function mergeRqtDraftWithBaseDefinition(base: GameDefinition, imported: GameDefinition): GameDefinition {
  const importedSummary = imported.stateSummaryTemplate?.trim();
  const defaultImportedSummary = defaultStateSummary(imported.initialState).trim();
  const next: GameDefinition = {
    ...base,
    title: imported.title || base.title,
    ...(imported.description !== undefined ? { description: imported.description } : {}),
    ...(imported.icon ? { icon: imported.icon } : {}),
    initialState: mergeRqtInitialState(base, imported),
    instructions: imported.instructions?.trim() ? imported.instructions : base.instructions,
    ...(importedSummary && importedSummary !== defaultImportedSummary
      ? { stateSummaryTemplate: imported.stateSummaryTemplate }
      : {}),
  };

  if (!base.dice && imported.dice) next.dice = imported.dice;
  if (!base.assets?.length && imported.assets?.length) next.assets = imported.assets;
  if (!base.assetBindings?.length && imported.assetBindings?.length) next.assetBindings = imported.assetBindings;
  if (!base.view && imported.view) next.view = imported.view;
  if (imported.sceneGraph?.scenes.length) next.sceneGraph = imported.sceneGraph;
  if (imported.stateSchema?.length) {
    next.stateSchema = mergeRqtStateSchema(base.stateSchema, imported.stateSchema);
    next.mutableKeys = imported.mutableKeys ?? [];
  }
  if (imported.clamps?.length) next.clamps = mergeRqtClamps(base.clamps, imported.clamps);
  return next;
}

function mergeRqtStateSchema(
  base: GameStateFieldSchema[] | undefined,
  imported: GameStateFieldSchema[],
): GameStateFieldSchema[] {
  const byKey = new Map((base ?? []).map((field) => [field.key, field]));
  for (const field of imported) byKey.set(field.key, field);
  return [...byKey.values()];
}

function mergeRqtClamps(base: GameClamp[] | undefined, imported: GameClamp[]): GameClamp[] {
  const byKey = new Map((base ?? []).map((clamp) => [clamp.key, clamp]));
  for (const clamp of imported) byKey.set(clamp.key, clamp);
  return [...byKey.values()];
}

function mergeRqtInitialState(base: GameDefinition, imported: GameDefinition): Record<string, unknown> {
  const schemaByKey = new Map((base.stateSchema ?? []).map((field) => [field.key, field]));
  const next = cloneRecord(base.initialState);
  for (const [key, value] of Object.entries(imported.initialState ?? {})) {
    if (isUnsafeObjectKey(key)) continue;
    if (RQT_DICE_RUNTIME_KEYS.has(key) && !(key in base.initialState)) continue;
    const field = schemaByKey.get(key);
    if (field && !valueMatchesField(value, field)) continue;
    setSafeRecordValue(next, key, value);
  }
  return next;
}

function valueMatchesField(value: unknown, field: GameStateFieldSchema): boolean {
  switch (field.valueType) {
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "boolean":
      return typeof value === "boolean";
    case "string":
      return typeof value === "string";
    case "enum":
      return typeof value === "string" && (!field.values?.length || field.values.includes(value));
    case "string[]":
      return Array.isArray(value) && value.every((item) => typeof item === "string");
    case "array":
    case "list":
      return Array.isArray(value);
    case "object":
      return !!value && typeof value === "object" && !Array.isArray(value);
    default:
      return true;
  }
}

function jsonLiteral(value: string): string {
  return JSON.stringify(value);
}

function normalizeRqtTemplateSyntax(template: string): string {
  return template.replace(/@\(\s*([\p{L}_][\p{L}\p{N}_.-]*)\s*\)/gu, "{{$1}}");
}

function packageIdForGameCard(card: GameCard): string {
  const publicId = card.share?.publicId?.replace(/[^A-Za-z0-9._-]/g, "_");
  if (publicId && /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(publicId)) {
    return publicId.includes(".") ? publicId : `creator.${publicId}`;
  }
  const title = slugAscii(card.definition.title) || "game";
  const id = slugAscii(card.id) || slugAscii(card.definition.id) || "package";
  return `creator.${title}_${id}`.slice(0, 96);
}

function slugAscii(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_")
    .toLowerCase();
}

function ratingFromPackage(value: unknown): ContentRating | undefined {
  return value === "sfw" || value === "nsfw" || value === "unrated" ? value : undefined;
}

function sourceRqtForCard(card: GameCard): string | undefined {
  if (card.extensions?.["sourceRqtStatus"] !== "current") return undefined;
  const source = card.extensions?.["sourceRqt"];
  if (typeof source !== "string") return undefined;
  if (!source.trim()) return undefined;
  return sourceMatchesCurrentSceneGraph(source, card.definition) ? source : undefined;
}

function sourceMatchesCurrentSceneGraph(source: string, definition: GameDefinition): boolean {
  try {
    const sourceDefinition = roqeetRqtToGameDefinition(source, "source.rqt");
    return sceneGraphSignature(gameDefinitionToRoqeetJson(sourceDefinition)) ===
      sceneGraphSignature(gameDefinitionToRoqeetJson(definition));
  } catch {
    return false;
  }
}

function roqeetGameDocumentForExport(card: GameCard): RoqeetGameDocument {
  if (!card.document) return gameDefinitionToRoqeetJson(card.definition);

  assertExportableRoqeetGameDocument(card.document);
  if (storedSceneGraphIsStale(card.document, card.definition)) {
    return gameDefinitionToRoqeetJson(card.definition);
  }
  return card.document;
}

function assertExportableRoqeetGameDocument(document: unknown): asserts document is RoqeetGameDocument {
  const errors = validateRoqeetGameDocument(document).filter((issue) => issue.severity === "error");
  if (errors.length === 0) return;
  throw new Error(`.rqg export validation failed: ${formatDocumentValidationErrors(errors)}`);
}

function formatDocumentValidationErrors(errors: ReturnType<typeof validateRoqeetGameDocument>): string {
  return errors
    .slice(0, 3)
    .map((issue) => issue.path ? `${issue.path}: ${issue.message}` : issue.message)
    .join(" / ");
}

function storedSceneGraphIsStale(document: RoqeetGameDocument, definition: GameDefinition): boolean {
  if (!definition.sceneGraph?.scenes.length) return false;
  const currentDocument = gameDefinitionToRoqeetJson(definition);
  return sceneGraphSignature(document) !== sceneGraphSignature(currentDocument);
}

function sceneGraphSignature(document: RoqeetGameDocument): string {
  return JSON.stringify({
    startSceneId: document.startSceneId ?? null,
    scenes: document.nodes
      .filter((node) => node.type === "game.scene")
      .map((scene) => ({
        id: scene.id,
        title: scene.title ?? null,
        content: scene.content,
        choices: scene.choices ?? [],
      })),
  });
}

function assertRqpRelevantTextEntriesWithinLimit(entries: ZipEntry[]): void {
  const total = entries.reduce((sum, entry) => sum + entry.uncompressedSize, 0);
  if (total <= RQP_TOTAL_TEXT_ENTRY_LIMIT_BYTES) return;
  throw new Error(
    `.rqp text entries are too large. Maximum total uncompressed size is ${formatMib(RQP_TOTAL_TEXT_ENTRY_LIMIT_BYTES)}.`,
  );
}

function assertRqpTextEntryWithinLimit(entry: ZipEntry, label: string): void {
  if (entry.uncompressedSize <= RQP_TEXT_ENTRY_LIMIT_BYTES) return;
  throw new Error(
    `.rqp entry "${label}" is too large. Maximum uncompressed size is ${formatMib(RQP_TEXT_ENTRY_LIMIT_BYTES)}.`,
  );
}

function assertRqpTextBytesWithinLimit(data: Uint8Array, label: string): void {
  if (data.byteLength <= RQP_TEXT_ENTRY_LIMIT_BYTES) return;
  throw new Error(
    `.rqp entry "${label}" is too large. Maximum uncompressed size is ${formatMib(RQP_TEXT_ENTRY_LIMIT_BYTES)}.`,
  );
}

function formatMib(bytes: number): string {
  return `${Math.round(bytes / MIB)} MB`;
}

async function readPackageSourceRqt(
  bytes: Uint8Array,
  sourcePath: string | undefined,
  entries = readZipEntries(bytes),
): Promise<string | undefined> {
  if (!sourcePath) return undefined;
  const entry = entries.find((item) => item.name === sourcePath);
  if (!entry) return undefined;
  assertRqpTextEntryWithinLimit(entry, sourcePath);
  const data = await readRqpTextEntryData(bytes, entry, sourcePath);
  if (!data) return undefined;
  assertRqpTextBytesWithinLimit(data, sourcePath);
  return TEXT_DECODER.decode(data);
}

async function readRqpTextEntryData(
  bytes: Uint8Array,
  entry: ZipEntry,
  label: string,
): Promise<Uint8Array | undefined> {
  try {
    return await readZipEntryData(bytes, entry, { maxBytes: RQP_TEXT_ENTRY_LIMIT_BYTES });
  } catch (error) {
    if (error instanceof ZipEntrySizeLimitError) {
      throw new Error(
        `.rqp entry "${label}" is too large. Maximum uncompressed size is ${formatMib(RQP_TEXT_ENTRY_LIMIT_BYTES)}.`,
      );
    }
    throw error;
  }
}

function makeStoredZip(files: { name: string; data: string | Uint8Array }[]): Uint8Array {
  const parts: number[] = [];
  const central: Array<{
    nameBytes: Uint8Array;
    dataBytes: Uint8Array;
    offset: number;
    crc: number;
  }> = [];
  const push16 = (n: number) => parts.push(n & 0xff, (n >>> 8) & 0xff);
  const push32 = (n: number) =>
    parts.push(n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff);
  const pushBytes = (b: Uint8Array) => {
    for (const x of b) parts.push(x);
  };

  for (const file of files) {
    const nameBytes = TEXT_ENCODER.encode(file.name);
    const dataBytes = typeof file.data === "string" ? TEXT_ENCODER.encode(file.data) : file.data;
    const offset = parts.length;
    const crc = crc32(dataBytes);
    central.push({ nameBytes, dataBytes, offset, crc });

    push32(0x04034b50);
    push16(20);
    push16(0);
    push16(0);
    push16(0);
    push16(0);
    push32(crc);
    push32(dataBytes.length);
    push32(dataBytes.length);
    push16(nameBytes.length);
    push16(0);
    pushBytes(nameBytes);
    pushBytes(dataBytes);
  }

  const cdOffset = parts.length;
  for (const file of central) {
    push32(0x02014b50);
    push16(20);
    push16(20);
    push16(0);
    push16(0);
    push16(0);
    push16(0);
    push32(file.crc);
    push32(file.dataBytes.length);
    push32(file.dataBytes.length);
    push16(file.nameBytes.length);
    push16(0);
    push16(0);
    push16(0);
    push16(0);
    push32(0);
    push32(file.offset);
    pushBytes(file.nameBytes);
  }
  const cdSize = parts.length - cdOffset;

  push32(0x06054b50);
  push16(0);
  push16(0);
  push16(files.length);
  push16(files.length);
  push32(cdSize);
  push32(cdOffset);
  push16(0);

  return new Uint8Array(parts);
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let i = 0; i < 8; i++) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export function formatRoqeetGameSource(definition: GameDefinition): string {
  return formatRoqeetGameDocument(gameDefinitionToRoqeetJson(definition));
}

/**
 * Legacy migration용: 예전 GameDefinition JSON 텍스트를 검증해 정의로 파싱한다. 오래된
 * 공유 파일/저장 데이터를 새 `.rqg` 경로로 흡수하기 위한 호환 함수다 —
 * sanitizeDefinition을 그대로 통과하므로 화이트리스트·필수 필드 검증이 동일하게
 * 적용된다(결정 #17). id는 빈 문자열로 두고 카드 팩토리/저장 단계가 재시드한다.
 */
export function parseGameDefinition(text: string): GameDefinition {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("JSON 구문 오류: 정의를 해석할 수 없습니다.");
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("게임 정의는 객체여야 합니다.");
  }
  return sanitizeDefinition(parsed as AnyObj);
}

/**
 * 임의 객체를 GameDefinition으로 화이트리스트 재구성한다(결정 #17). 필수 필드
 * (title·initialState·instructions)가 없으면 거부한다. id는 빈 문자열로 두고
 * 팩토리가 재시드한다(결정 #29).
 */
function sanitizeDefinition(src: AnyObj): GameDefinition {
  const title = typeof src["title"] === "string" ? (src["title"] as string).trim() : "";
  const instructions =
    typeof src["instructions"] === "string" ? (src["instructions"] as string) : "";
  const initialState =
    src["initialState"] && typeof src["initialState"] === "object" && !Array.isArray(src["initialState"])
      ? (src["initialState"] as AnyObj)
      : undefined;

  if (!title) throw new Error("유효한 게임 카드가 아닙니다(title 없음).");
  if (!instructions) throw new Error("유효한 게임 카드가 아닙니다(instructions 없음).");
  if (!initialState) throw new Error("유효한 게임 카드가 아닙니다(initialState 없음).");

  const def: GameDefinition = {
    id: "", // 팩토리가 카드 id로 재시드(결정 #29).
    title,
    initialState: sanitizeState(initialState),
    instructions,
  };

  if (typeof src["description"] === "string") def.description = src["description"];
  if (typeof src["titleKey"] === "string") def.titleKey = src["titleKey"];
  if (typeof src["descriptionKey"] === "string") def.descriptionKey = src["descriptionKey"];
  if (typeof src["icon"] === "string") def.icon = src["icon"];
  if (typeof src["instructionsKey"] === "string") def.instructionsKey = src["instructionsKey"];
  if (typeof src["stateSummaryTemplate"] === "string") {
    def.stateSummaryTemplate = src["stateSummaryTemplate"];
  }
  if (typeof src["stateSummaryTemplateKey"] === "string") {
    def.stateSummaryTemplateKey = src["stateSummaryTemplateKey"];
  }
  const setup = sanitizeSetup(src["setup"]);
  if (setup.length) def.setup = setup;
  const stateSchema = sanitizeStateSchema(src["stateSchema"]);
  if (stateSchema.length) def.stateSchema = stateSchema;
  if (Array.isArray(src["mutableKeys"])) {
    def.mutableKeys = (src["mutableKeys"] as unknown[])
      .filter((k): k is string => typeof k === "string")
      .map((k) => k.trim())
      .filter(Boolean);
  }
  const clamps = sanitizeClamps(src["clamps"]);
  if (clamps.length) def.clamps = clamps;

  const rules = sanitizeRules(src["rules"]);
  if (rules.length) def.rules = rules;
  const initRules = sanitizeRules(src["initRules"]);
  if (initRules.length) def.initRules = initRules;
  const gates = sanitizeGates(src["gates"]);
  if (gates.length) def.gates = gates;

  const entityTemplate = sanitizeEntityTemplate(src["entityTemplate"]);
  if (entityTemplate) def.entityTemplate = entityTemplate;
  const entityRoster = sanitizeEntityRoster(src["entityRoster"]);
  if (entityRoster) def.entityRoster = entityRoster;
  if (Array.isArray(src["mutableEntityKeys"])) {
    def.mutableEntityKeys = (src["mutableEntityKeys"] as unknown[])
      .filter((k): k is string => typeof k === "string")
      .map((k) => k.trim())
      .filter(Boolean);
  }
  const entityClamps = sanitizeClamps(src["entityClamps"]);
  if (entityClamps.length) def.entityClamps = entityClamps;

  const dice = sanitizeDice(src["dice"]);
  if (dice) def.dice = dice;

  const endWhen = sanitizeEndConditions(src["endWhen"]);
  if (endWhen.length) def.endWhen = endWhen;

  const assets = sanitizeAssets(src["assets"]);
  if (assets.length) def.assets = assets;
  const assetBindings = sanitizeAssetBindings(src["assetBindings"]);
  if (assetBindings.length) def.assetBindings = assetBindings;
  const hud = sanitizeHud(src["hud"]);
  if (hud) def.hud = hud;
  const view = sanitizeView(src["view"]);
  if (view) def.view = view;

  return def;
}

/** 초기 상태는 직렬화 가능한 원시값/배열만 통과시킨다(결정 #28). */
function sanitizeState(src: AnyObj): AnyObj {
  const out: AnyObj = {};
  for (const [key, v] of Object.entries(src)) {
    if (isUnsafeObjectKey(key)) continue;
    if (
      typeof v === "number" ||
      typeof v === "string" ||
      typeof v === "boolean" ||
      v === null ||
      Array.isArray(v)
    ) {
      setSafeRecordValue(out, key, v);
    } else if (typeof v === "object") {
      // 중첩 객체는 얕게 허용(JSON 직렬화 가능 가정). 깊은 검증은 과하므로 통과.
      setSafeRecordValue(out, key, v);
    }
  }
  return out;
}

function sanitizeClamps(src: unknown): GameClamp[] {
  if (!Array.isArray(src)) return [];
  const out: GameClamp[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["key"] !== "string" || !o["key"].trim()) continue;
    const clamp: GameClamp = { key: o["key"].trim() };
    if (typeof o["min"] === "number") clamp.min = o["min"];
    if (typeof o["max"] === "number") clamp.max = o["max"];
    out.push(clamp);
  }
  return out;
}

function sanitizeSetup(src: unknown): GameSetupField[] {
  if (!Array.isArray(src)) return [];
  const out: GameSetupField[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["key"] !== "string" || !o["key"].trim()) continue;
    const options = sanitizeSetupOptions(o["options"]);
    if (!options.length) continue;
    const field: GameSetupField = { key: o["key"].trim(), options };
    if (typeof o["label"] === "string") field.label = o["label"];
    if (typeof o["default"] === "string") field.default = o["default"];
    out.push(field);
  }
  return out;
}

function sanitizeSetupOptions(src: unknown): GameSetupOption[] {
  if (!Array.isArray(src)) return [];
  const out: GameSetupOption[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["value"] !== "string") continue;
    const option: GameSetupOption = { value: o["value"] };
    if (typeof o["label"] === "string") option.label = o["label"];
    out.push(option);
  }
  return out;
}

function sanitizeStateSchema(src: unknown): GameStateFieldSchema[] {
  if (!Array.isArray(src)) return [];
  const out: GameStateFieldSchema[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["key"] !== "string" || !o["key"].trim()) continue;
    if (typeof o["valueType"] !== "string" || !STATE_VALUE_TYPES.has(o["valueType"] as GameStateValueType)) continue;
    const field: GameStateFieldSchema = {
      key: o["key"].trim(),
      valueType: o["valueType"] as GameStateValueType,
    };
    if (isJsonValue(o["default"])) field.default = o["default"];
    if (typeof o["min"] === "number") field.min = o["min"];
    if (typeof o["max"] === "number") field.max = o["max"];
    if (o["mutableBy"] === "ai" || o["mutableBy"] === "engine" || o["mutableBy"] === "readonly") {
      field.mutableBy = o["mutableBy"];
    }
    if (Array.isArray(o["values"])) {
      const values = (o["values"] as unknown[]).filter((v): v is string => typeof v === "string");
      if (values.length) field.values = values;
    }
    if (typeof o["mutable"] === "boolean") field.mutable = o["mutable"];
    if (typeof o["derived"] === "boolean") field.derived = o["derived"];
    if (typeof o["transient"] === "boolean") field.transient = o["transient"];
    if (typeof o["label"] === "string") field.label = o["label"];
    out.push(field);
  }
  return out;
}

function sanitizeEntityTemplate(src: unknown): GameEntityTemplate | undefined {
  if (!src || typeof src !== "object" || Array.isArray(src)) return undefined;
  const out: GameEntityTemplate = {};
  for (const [key, value] of Object.entries(src as AnyObj)) {
    if (isUnsafeObjectKey(key)) continue;
    if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
      out[key] = value;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function sanitizeEntityRoster(src: unknown): GameEntityRoster | undefined {
  if (!src || typeof src !== "object" || Array.isArray(src)) return undefined;
  const out: GameEntityRoster = {};
  for (const [name, fields] of Object.entries(src as AnyObj)) {
    if (isUnsafeObjectKey(name)) continue;
    const clean = sanitizeEntityTemplate(fields);
    if (clean) out[name] = clean;
  }
  return Object.keys(out).length ? out : undefined;
}

/** 조건부 규칙을 화이트리스트로 재구성한다(결정 #34/#17). 알 수 없는 연산자는 거른다. */
function sanitizeRules(src: unknown): GameRule[] {
  if (!Array.isArray(src)) return [];
  const out: GameRule[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;

    // when: 조건 배열(빈 배열 허용 = 항상 발동).
    const when: GameCondition[] = [];
    if (Array.isArray(o["when"])) {
      for (const c of o["when"]) {
        if (!c || typeof c !== "object") continue;
        const co = c as AnyObj;
        if (typeof co["key"] !== "string" || !co["key"].trim()) continue;
        if (typeof co["op"] !== "string" || !CONDITION_OPS.has(co["op"] as GameConditionOp)) continue;
        const cond: GameCondition = { key: co["key"].trim(), op: co["op"] as GameConditionOp };
        const v = co["value"];
        if (typeof v === "number" || typeof v === "string" || typeof v === "boolean") cond.value = v;
        when.push(cond);
      }
    }

    // then: 액션 배열(최소 1개 유효 액션이 있어야 규칙을 채택).
    const then: GameRuleAction[] = [];
    if (Array.isArray(o["then"])) {
      for (const a of o["then"]) {
        if (!a || typeof a !== "object") continue;
        const ao = a as AnyObj;
        if (typeof ao["op"] !== "string" || !RULE_ACTION_OPS.has(ao["op"])) continue;
        if (typeof ao["key"] !== "string" || !ao["key"].trim()) continue;
        const v = ao["value"];
        if (typeof v !== "number" && typeof v !== "string" && typeof v !== "boolean") continue;
        then.push({ op: ao["op"] as GameRuleAction["op"], key: ao["key"].trim(), value: v });
      }
    }

    if (then.length > 0) out.push({ when, then });
  }
  return out;
}

function sanitizeGates(src: unknown): GameGate[] {
  if (!Array.isArray(src)) return [];
  const out: GameGate[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["inject"] !== "string") continue;
    out.push({ when: sanitizeConditions(o["when"]), inject: o["inject"] });
  }
  return out;
}

function sanitizeConditions(src: unknown): GameCondition[] {
  if (!Array.isArray(src)) return [];
  const out: GameCondition[] = [];
  for (const c of src) {
    if (!c || typeof c !== "object") continue;
    const co = c as AnyObj;
    if (typeof co["key"] !== "string" || !co["key"].trim()) continue;
    if (typeof co["op"] !== "string" || !CONDITION_OPS.has(co["op"] as GameConditionOp)) continue;
    const cond: GameCondition = { key: co["key"].trim(), op: co["op"] as GameConditionOp };
    const v = co["value"];
    if (typeof v === "number" || typeof v === "string" || typeof v === "boolean") cond.value = v;
    out.push(cond);
  }
  return out;
}

function sanitizeDice(src: unknown): GameDiceRule | undefined {
  if (!src || typeof src !== "object") return undefined;
  const o = src as AnyObj;
  const dice: GameDiceRule = {};
  if (typeof o["resultKey"] === "string") dice.resultKey = o["resultKey"];
  if (typeof o["notationKey"] === "string") dice.notationKey = o["notationKey"];
  if (typeof o["dcKey"] === "string") dice.dcKey = o["dcKey"];
  if (typeof o["successKey"] === "string") dice.successKey = o["successKey"];
  return dice;
}

function sanitizeEndConditions(src: unknown): GameEndCondition[] {
  if (!Array.isArray(src)) return [];
  const out: GameEndCondition[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["key"] !== "string" || !o["key"].trim()) continue;
    if (typeof o["op"] !== "string" || !CONDITION_OPS.has(o["op"] as GameConditionOp)) continue;
    const cond: GameEndCondition = { key: o["key"].trim(), op: o["op"] as GameConditionOp };
    const val = o["value"];
    if (typeof val === "number" || typeof val === "string" || typeof val === "boolean") {
      cond.value = val;
    }
    if (typeof o["label"] === "string") cond.label = o["label"];
    if (typeof o["notice"] === "string") cond.notice = o["notice"];
    if (o["tone"] === "info" || o["tone"] === "success" || o["tone"] === "warning" || o["tone"] === "danger") {
      cond.tone = o["tone"];
    }
    out.push(cond);
  }
  return out;
}

function sanitizeAssets(src: unknown): GameAssetRegistryEntry[] {
  if (!Array.isArray(src)) return [];
  const out: GameAssetRegistryEntry[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["key"] !== "string" || !o["key"].trim()) continue;
    if (typeof o["kind"] !== "string" || !ASSET_KINDS.has(o["kind"])) continue;
    const asset: GameAssetRegistryEntry = {
      key: o["key"].trim(),
      kind: o["kind"] as GameAssetRegistryEntry["kind"],
    };
    if (typeof o["assetId"] === "string") asset.assetId = o["assetId"];
    if (typeof o["label"] === "string") asset.label = o["label"];
    out.push(asset);
  }
  return out;
}

function sanitizeAssetBindings(src: unknown): GameAssetBinding[] {
  if (!Array.isArray(src)) return [];
  const out: GameAssetBinding[] = [];
  for (const item of src) {
    if (!item || typeof item !== "object") continue;
    const o = item as AnyObj;
    if (typeof o["id"] !== "string" || !o["id"].trim()) continue;
    if (typeof o["slot"] !== "string" || !ASSET_SLOTS.has(o["slot"])) continue;
    const binding: GameAssetBinding = {
      id: o["id"].trim(),
      slot: o["slot"] as GameAssetBinding["slot"],
    };
    if (typeof o["assetKey"] === "string") binding.assetKey = o["assetKey"];
    if (typeof o["assetId"] === "string") binding.assetId = o["assetId"];
    const when = sanitizeConditions(o["when"]);
    if (when.length) binding.when = when;
    if (typeof o["characterId"] === "string") binding.characterId = o["characterId"];
    if (typeof o["emotion"] === "string") binding.emotion = o["emotion"];
    if (typeof o["position"] === "string" && ASSET_POSITIONS.has(o["position"])) {
      binding.position = o["position"] as GameAssetBinding["position"];
    }
    out.push(binding);
  }
  return out;
}

function sanitizeHud(src: unknown): GameHudDeclaration | undefined {
  const view = sanitizeView(src);
  if (!view) return undefined;
  const hud: GameHudDeclaration = {};
  if (view.statBars) hud.statBars = view.statBars;
  if (view.statPanel) hud.statPanel = view.statPanel;
  if (view.inventoryKey) hud.inventoryKey = view.inventoryKey;
  if (view.inventoryTitle) hud.inventoryTitle = view.inventoryTitle;
  if (view.inventoryTitleKey) hud.inventoryTitleKey = view.inventoryTitleKey;
  return Object.keys(hud).length ? hud : undefined;
}

function sanitizeView(src: unknown): GameViewBinding | undefined {
  if (!src || typeof src !== "object") return undefined;
  const o = src as AnyObj;
  const view: GameViewBinding = {};

  if (Array.isArray(o["statBars"])) {
    const bars: GameStatBarBinding[] = [];
    for (const item of o["statBars"]) {
      if (!item || typeof item !== "object") continue;
      const b = item as AnyObj;
      if (typeof b["label"] !== "string" || typeof b["valueKey"] !== "string") continue;
      const bar: GameStatBarBinding = { label: b["label"], valueKey: b["valueKey"] };
      if (typeof b["maxKey"] === "string") bar.maxKey = b["maxKey"];
      if (typeof b["max"] === "number") bar.max = b["max"];
      if (typeof b["tone"] === "string") bar.tone = b["tone"];
      bars.push(bar);
    }
    if (bars.length) view.statBars = bars;
  }

  if (Array.isArray(o["statPanel"])) {
    const panel: GameStatPanelBinding[] = [];
    for (const item of o["statPanel"]) {
      if (!item || typeof item !== "object") continue;
      const p = item as AnyObj;
      if (typeof p["label"] !== "string" || typeof p["key"] !== "string") continue;
      panel.push({ label: p["label"], key: p["key"] });
    }
    if (panel.length) view.statPanel = panel;
  }

  if (typeof o["inventoryKey"] === "string") view.inventoryKey = o["inventoryKey"];
  if (typeof o["inventoryTitle"] === "string") view.inventoryTitle = o["inventoryTitle"];
  if (typeof o["inventoryTitleKey"] === "string") view.inventoryTitleKey = o["inventoryTitleKey"];
  if (typeof o["showChoices"] === "boolean") view.showChoices = o["showChoices"];
  if (typeof o["showDice"] === "boolean") view.showDice = o["showDice"];

  return Object.keys(view).length ? view : undefined;
}

function collectRqtEnv(result: RoqeetRunResult): Record<string, RoqeetValue> {
  const out: Record<string, RoqeetValue> = {};
  for (const node of result.json.nodes) {
    if (node.type !== "env") continue;
    copySafeRecordEntries(out, node.values);
  }
  return out;
}

function collectRqtGameMeta(result: RoqeetRunResult): Record<string, RoqeetValue> {
  const out: Record<string, RoqeetValue> = {};
  for (const node of result.json.nodes) {
    if (node.type !== "game") continue;
    copySafeRecordEntries(out, node.values);
  }
  return out;
}

function collectRqtStateSchema(result: RoqeetRunResult): {
  fields: GameStateFieldSchema[];
  initialState: Record<string, unknown>;
} {
  const fields: GameStateFieldSchema[] = [];
  const initialState: Record<string, unknown> = {};
  for (const node of result.json.nodes) {
    if (node.type !== "state") continue;
    const field = rqtStateDeclarationToGameField(node.field);
    if (isUnsafeObjectKey(field.key)) continue;
    fields.push(field);
    setSafeRecordValue(initialState, field.key, node.field.initial);
  }
  return { fields, initialState };
}

function rqtStateDeclarationToGameField(declaration: RoqeetStateDeclaration): GameStateFieldSchema {
  const field: GameStateFieldSchema = {
    key: declaration.key,
    valueType: declaration.valueType,
    default: cloneUnknown(declaration.initial) as GameStateFieldSchema["default"],
  };
  if (declaration.min !== undefined) field.min = declaration.min;
  if (declaration.max !== undefined) field.max = declaration.max;
  if (declaration.values?.length) field.values = [...declaration.values];
  if (declaration.mutableBy !== undefined) field.mutableBy = declaration.mutableBy;
  if (declaration.mutable !== undefined) field.mutable = declaration.mutable;
  if (declaration.derived !== undefined) field.derived = declaration.derived;
  if (declaration.transient !== undefined) field.transient = declaration.transient;
  if (declaration.label !== undefined) field.label = declaration.label;
  return field;
}

function isRqtAiMutableField(field: GameStateFieldSchema): boolean {
  if (field.derived) return false;
  if (field.mutableBy !== undefined) return field.mutableBy === "ai";
  return field.mutable === true;
}

function stringEnv(env: Record<string, RoqeetValue>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = env[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

function stringArrayEnv(env: Record<string, RoqeetValue>, keys: string[]): string[] | undefined {
  for (const key of keys) {
    const value = env[key];
    if (Array.isArray(value)) {
      const strings = value.filter((item): item is string => typeof item === "string").map((item) => item.trim()).filter(Boolean);
      if (strings.length) return strings;
    }
  }
  return undefined;
}

function buildRqtInstructions(
  result: RoqeetRunResult,
  env: Record<string, RoqeetValue>,
  appendDefaultGuide: boolean,
): string {
  const explicit = stringEnv(env, ["instructions", "guide", "prompt", "\uC9C0\uC2DC\uBB38", "\uADDC\uCE59"]);
  const promptBlocks = result.prompts
    .map((prompt) => (
      prompt.name === "guide" || prompt.name === "main"
        ? prompt.content
        : `[${prompt.name}]\n${prompt.content}`
    ).trim())
    .filter(Boolean)
    .join("\n\n");
  const parts = [explicit, promptBlocks].filter((part): part is string => !!part?.trim());
  const current = parts.join("\n\n");
  const defaultGuide = [
    "AI writes narration and may propose explicit RoQeet actions.",
    "RoQeet Engine validates and applies state, choice, scene, asset, dice, and save behavior.",
    "Allowed action commands include @set/@increase/@decrease/@push/@unset/@roll and their Korean aliases.",
    "Do not rely on hidden HTML, image tags, or regex-only state tags for core game state.",
  ].join("\n");
  if (appendDefaultGuide && !current.includes("RoQeet Engine validates and applies")) {
    parts.push(defaultGuide);
  }
  return parts.join("\n\n");
}

function buildRqtView(state: Record<string, unknown>, result: RoqeetRunResult): GameViewBinding {
  const keys = Object.keys(state);
  const numericKeys = keys.filter((key) => typeof state[key] === "number" && !key.toLowerCase().startsWith("max"));
  const bars: GameStatBarBinding[] = numericKeys.slice(0, 3).map((key) => {
    const maxKey = findMaxKey(keys, key);
    return {
      label: labelFromKey(key),
      valueKey: key,
      ...(maxKey ? { maxKey } : {}),
      tone: toneForKey(key),
    };
  });
  const statPanel = keys
    .filter((key) => !Array.isArray(state[key]) && state[key] !== null && typeof state[key] !== "object")
    .slice(0, 8)
    .map((key) => ({ label: labelFromKey(key), key }));
  const inventoryKey = keys.find((key) => Array.isArray(state[key]));
  return {
    ...(bars.length ? { statBars: bars } : {}),
    ...(statPanel.length ? { statPanel } : {}),
    ...(inventoryKey ? { inventoryKey, inventoryTitle: labelFromKey(inventoryKey) } : {}),
    showChoices: true,
    ...(result.rolls.length || state["lastRoll"] !== undefined ? { showDice: true } : {}),
  };
}

function buildRqtAssets(result: RoqeetRunResult): GameAssetRegistryEntry[] {
  return collectAssetKeys(result).map((key) => ({
    key,
    kind: imageLikeAsset(key) ? "image" : "other",
    assetId: key,
  }));
}

function buildRqtAssetBindings(result: RoqeetRunResult): GameAssetBinding[] {
  return collectAssetKeys(result).map((key, index) => {
    const isBackground = backgroundLikeAsset(key);
    const binding: GameAssetBinding = {
      id: `rqt.asset.${index}.${safeId(key)}`,
      slot: isBackground ? "background" : "sprite",
      assetKey: key,
    };
    if (!isBackground) binding.position = "center";
    return binding;
  });
}

function collectAssetKeys(result: RoqeetRunResult): string[] {
  const keys: string[] = [];
  for (const media of result.media) {
    if (media.asset.trim()) keys.push(media.asset.trim());
  }
  for (const scene of result.scenes) {
    for (const [key, value] of Object.entries(scene.props)) {
      if (/(^bg$|background|\uBC30\uACBD)/i.test(key) && value.trim()) keys.push(value.trim());
    }
  }
  return Array.from(new Set(keys));
}

function defaultStateSummary(state: Record<string, unknown>): string {
  const keys = Object.keys(state).slice(0, 8);
  return keys.length ? keys.map((key) => `${labelFromKey(key)} {{${key}}}`).join(", ") : "turn {{turn}}";
}

function titleFromFilename(filename: string): string {
  const base = filename.split(/[\\/]/).pop()?.replace(/\.(?:rqt|txt)$/i, "").trim();
  return base || "RoQeet Game";
}

function labelFromKey(key: string): string {
  return key.replace(/[._-]+/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function findMaxKey(keys: string[], key: string): string | undefined {
  const normalized = new Map(keys.map((item) => [item.toLowerCase(), item]));
  const capitalized = key.charAt(0).toUpperCase() + key.slice(1);
  return (
    normalized.get(`max${key.toLowerCase()}`) ??
    normalized.get(`max${capitalized}`.toLowerCase()) ??
    normalized.get(`${key.toLowerCase()}max`) ??
    normalized.get(`${key.toLowerCase()}_max`)
  );
}

function toneForKey(key: string): string {
  const lower = key.toLowerCase();
  if (lower.includes("hp") || lower.includes("health") || lower.includes("danger")) return "hp";
  if (lower.includes("mp") || lower.includes("mana") || lower.includes("sanity")) return "mp";
  if (lower.includes("affection") || lower.includes("trust") || lower.includes("progress")) return "success";
  return "info";
}

function imageLikeAsset(key: string): boolean {
  return /\.(?:png|jpe?g|webp|gif|svg|avif)$/i.test(key) || !/\.(?:mp3|wav|ogg|mp4|webm)$/i.test(key);
}

function backgroundLikeAsset(key: string): boolean {
  return /(^|[._/-])(?:bg|background|forest|room|scene|map)([._/-]|$)/i.test(key);
}

function safeId(value: string): string {
  return value.replace(/[^A-Za-z0-9_.-]+/g, "_").slice(0, 48) || "asset";
}

function cloneRecord(src: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  copySafeRecordEntries(out, src);
  return out;
}

function cloneUnknown(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneUnknown);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    copySafeRecordEntries(out, value as Record<string, unknown>);
    return out;
  }
  return value;
}

function copySafeRecordEntries(target: Record<string, unknown>, source: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(source)) {
    setSafeRecordValue(target, key, value);
  }
}

function setSafeRecordValue(target: Record<string, unknown>, key: string, value: unknown): void {
  if (isUnsafeObjectKey(key)) return;
  target[key] = cloneUnknown(value);
}

function isUnsafeObjectKey(key: string): boolean {
  return UNSAFE_OBJECT_KEYS.has(key);
}

function isJsonValue(value: unknown): value is GameStateFieldSchema["default"] {
  if (
    value === undefined ||
    value === null ||
    typeof value === "number" ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (Array.isArray(value)) return value.every(isJsonValue);
  if (typeof value === "object") return Object.values(value as AnyObj).every(isJsonValue);
  return false;
}
